import * as React from "react";

import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";

import type { RecommendedContent } from "@volley/data";

import BrowseAllCard from "./BrowseAllCard";
import ContentResultCard from "./ContentResultCard";
import RatingRow from "./RatingRow";
import type { ThreadMessage } from "./types";

const GREEN = "#3CE97C";
const NAVY_DEEP = "#0B1730";

export function Avatar({ size }: { size: number }): React.JSX.Element {
    return (
        <Box
            sx={{
                width: size,
                height: size,
                borderRadius: "50%",
                flexShrink: 0,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                color: NAVY_DEEP,
                background:
                    "radial-gradient(circle at 30% 30%, #58F08C 0%, #1FA557 60%, #0E6E3A 100%)",
            }}
        >
            <AutoAwesomeIcon sx={{ fontSize: size * 0.55 }} />
        </Box>
    );
}

interface ThreadMessageViewProps {
    message: ThreadMessage;
    /** Open a recommended item, leaving the chat. */
    onStart: (content: RecommendedContent) => void;
    /** Leave for the full workouts library via the trailing carousel card. */
    onBrowseAll: () => void;
    /**
     * Hand this conversation to a human. Takes the origin so the funnel can
     * tell an unanswerable question from one we simply got wrong, and the
     * player's own words as the fallback first message when there is no
     * transcript to send.
     */
    onEscalate: (
        origin: "help" | "unroutable" | "error",
        question?: string,
    ) => void;
    /** Record whether an answer helped. */
    onRate: (askId: number, helpful: boolean) => void;
    /** Put the cursor in the composer after an answer missed. */
    onRetry: () => void;
}

/**
 * One entry of the Ask Volley thread.
 *
 * Lifted out of the overlay's render body, where the branch-per-kind and its
 * nested JSX made the component itself hard to read. Nothing here knows about
 * the prompt, the impression or the conversation — it renders a message and
 * reports taps.
 */
export default function ThreadMessageView({
    message,
    onStart,
    onBrowseAll,
    onEscalate,
    onRate,
    onRetry,
}: ThreadMessageViewProps): React.JSX.Element {
    if (message.kind === "rating") {
        return (
            <RatingRow
                intent={message.intent}
                helpful={message.helpful}
                onRate={(helpful) => onRate(message.askId, helpful)}
                onRetry={onRetry}
                onEscalate={() => onEscalate("help")}
            />
        );
    }

    if (message.kind === "assistant") {
        return (
            <Stack
                direction="row"
                sx={{
                    gap: 1,
                    px: 2,
                    mb: 1.75,
                    alignItems: "flex-end",
                }}
            >
                <Avatar size={28} />
                <Box
                    sx={{
                        maxWidth: "78%",
                        px: 1.75,
                        py: 1.5,
                        borderRadius: 2,
                        borderBottomLeftRadius: 6,
                        bgcolor: "#182B57",
                        fontSize: 15,
                        lineHeight: 1.4,
                    }}
                >
                    {message.content}
                </Box>
            </Stack>
        );
    }
    if (message.kind === "support") {
        return (
            <Stack
                direction="row"
                sx={{
                    gap: 1,
                    px: 2,
                    mb: 1.75,
                    alignItems: "flex-end",
                }}
            >
                <Avatar size={28} />
                <Box
                    sx={{
                        maxWidth: "78%",
                        px: 1.75,
                        py: 1.5,
                        borderRadius: 2,
                        borderBottomLeftRadius: 6,
                        bgcolor: "#182B57",
                        fontSize: 15,
                        lineHeight: 1.4,
                    }}
                >
                    {message.content}
                    <Box
                        component="button"
                        type="button"
                        // The player's own words travel as the fallback first
                        // message: this is the one path where we know the
                        // question and are admitting we cannot answer it.
                        onClick={() =>
                            onEscalate(message.origin, message.question)
                        }
                        sx={{
                            display: "block",
                            mt: 1,
                            p: 0,
                            border: 0,
                            bgcolor: "transparent",
                            color: GREEN,
                            fontFamily: "inherit",
                            fontSize: 14,
                            fontWeight: 600,
                            cursor: "pointer",
                        }}
                    >
                        Message support
                    </Box>
                </Box>
            </Stack>
        );
    }
    if (message.kind === "user") {
        return (
            <Stack
                direction="row"
                sx={{
                    px: 2,
                    mb: 1.75,
                    justifyContent: "flex-end",
                }}
            >
                <Box
                    sx={{
                        maxWidth: "78%",
                        px: 1.75,
                        py: 1.5,
                        borderRadius: 2,
                        borderBottomRightRadius: 6,
                        bgcolor: "#fff",
                        color: NAVY_DEEP,
                        fontWeight: 500,
                        fontSize: 15,
                        lineHeight: 1.4,
                    }}
                >
                    {message.content}
                </Box>
            </Stack>
        );
    }
    if (message.kind === "loading") {
        return (
            <Stack
                direction="row"
                sx={{
                    gap: 1,
                    px: 2,
                    mb: 1.75,
                    alignItems: "center",
                }}
            >
                <Avatar size={28} />
                <CircularProgress size={18} sx={{ color: GREEN }} />
            </Stack>
        );
    }
    // carousel
    return (
        <Stack
            direction="row"
            sx={{
                gap: 1.5,
                px: 2,
                pb: 1.5,
                mb: 0.5,
                overflowX: "auto",
                scrollSnapType: "x proximity",
                "&::-webkit-scrollbar": { display: "none" },
                scrollbarWidth: "none",
            }}
        >
            {message.cards.map((card) => (
                <ContentResultCard
                    key={`${card.type}-${card.id}`}
                    content={card}
                    onStart={onStart}
                />
            ))}
            {/* Trailing "browse all workouts" card — offered
                after workout recommendations, but not after
                support articles, where routing someone to the
                workout library is a non sequitur. */}
            {message.intent !== "HELP" && (
                <BrowseAllCard onBrowseAll={onBrowseAll} />
            )}
        </Stack>
    );
}
